import * as React from 'react';
import * as styles from './clients.module.scss';
import { AppContext } from '../../../shared/app-context';
import CircularProgress from '@material-ui/core/CircularProgress';
import usePaginatedClients from '../../../hooks/usePaginatedClients';
import { PaginatedClientContext } from '../../../shared/paginated-client-context';
import useMeasuredRef from '../../../hooks/useMeasuredRef';
import ClientTable from './clientTable/client-table';
import ClientPagination from '../pagination/pagination';
import SearchBar from '../../common/search-bar';
import useSearch from '../../../hooks/useSearch';
import SecondaryButton from '../../common/secondary-button';
import { Redirect } from 'react-router-dom';
import Paths from '../../../models/enums/paths';
import useUser from '../../../hooks/useUser';

/**
 * Management
 * @function Management - Outer component for the management page
 */
const Management = () => {
  const { state } = React.useContext(AppContext);
  const { pageSize, currentPage, orderColumn, orderDirection } = React.useContext(PaginatedClientContext);
  const { loading, results, totalResults, fetchClientsPage } = usePaginatedClients();
  const { searchQuery: searchTerm, updateSearchQuery, setIsSearching } = useSearch();

  const announcementCount: number = +!!state.clientAnnouncement + +!!state.globalAnnouncement;
  const { measuredRef, dimensions } = useMeasuredRef([results, announcementCount], true);
  const [redirectToAddClient, setRedirectToAddClient] = React.useState(false);
  const { fetchCurrentUserRole, currentUserRole, loadingAssociatedData } = useUser();
  const [userRole, setUserRole] = React.useState<string>('');

  // Fetches the client information for the page on mount
  React.useEffect(() => {
    fetchClientsPage({ currentPage, pageSize, searchTerm, orderColumn, orderDirection });
    fetchCurrentUserRole();
  }, []);

  React.useEffect(() => {
    if (!loadingAssociatedData) {
      setUserRole(currentUserRole);
    }
  }, [loadingAssociatedData]);

  const canAddClient = () => {
    return userRole === 'Elite Admin' || userRole === 'Super Admin';
  };

  if (redirectToAddClient) {
    return <Redirect push to={{ pathname: Paths.AddClient }} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Clients</h1>
          {canAddClient() && (
            <SecondaryButton
              text="Add a Client"
              title="Add a Client"
              handleClick={() => setRedirectToAddClient(true)}
            />
          )}
        </div>
        <SearchBar
          value={searchTerm}
          updateValue={(val: string) => updateSearchQuery(val)}
          setIsSearching={(searching: boolean) => setIsSearching(searching)}
          placeholder={'Search Clients'}
          submitHandler={() =>
            fetchClientsPage({
              currentPage: 1,
              pageSize: pageSize,
              searchTerm,
              orderColumn,
              orderDirection
            })
          }
        />
      </div>
      {loading && results.length === 0 ? (
        <div className={styles.circularProgress}>
          <CircularProgress />
        </div>
      ) : !results || results.length === 0 ? (
        <span>There are no clients.</span>
      ) : (
        <div
          className={styles.center}
          ref={measuredRef}
          style={{ height: `calc(100vh - ${dimensions ? 20 + dimensions.top : 0}px` }}
        >
          {(loading && results.length > 0) || (loading && results.length === 0) ? (
            <div className={styles.circularProgress}>
              <CircularProgress />
            </div>
          ) : null}
          <div className={styles.content}>
            <div>
              <span className={styles.resultsCount}>{totalResults} results</span>
            </div>
            {<ClientTable loading={loading || loadingAssociatedData} isClientAdmin={userRole === 'Client Admin'} />}
          </div>
          <ClientPagination
            fetchResults={fetchClientsPage}
            paginatedContext={PaginatedClientContext}
            searchTerm={searchTerm}
          />
        </div>
      )}
    </div>
  );
};

export default Management;
